
import { useState, useEffect, useCallback, useRef } from 'react';
import { AppState } from '../types';
import { loadAppState, saveAppState } from '../services/storage.service';
import { migrate, CURRENT_VERSION } from '../services/migration.service';
import { getTodayDateString } from '../utils/date';
import { INITIAL_TOKENS, ALL_QUESTS } from '../constants';
import { useAuthStore } from '../store/authStore';

// The default state for a brand new user.
export const getInitialState = (): AppState => ({
  version: CURRENT_VERSION,
  date: getTodayDateString(),
  tokens: INITIAL_TOKENS,
  history: { [getTodayDateString()]: { tokens: INITIAL_TOKENS, questsCompleted: [] } },
  questTokenEarnedToday: false,
  dailyChecklistCompleted: { clothes: false, bed: false, dressed: false },
  theme: 'sky',
  tokenIcon: '🌟',
  backgroundImage: null,
  backgroundImageSizing: 'cover',
  quests: ALL_QUESTS,
  rejectedQuests: [],
  activeQuests: [],
  offeredQuests: {},
  completedQuestIds: [],
  lastDrawnQuestIds: {},
});

export const useAppState = () => {
  const [appState, setAppState] = useState<AppState | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const user = useAuthStore(state => state.user);
  const hasSaved = useRef(false);

  const loadInitialState = useCallback(async () => {
    let state = await loadAppState();
    if (state) {
      state = migrate(state);

      const today = getTodayDateString();
      if (state.date !== today) {
        state.date = today;
        state.tokens = INITIAL_TOKENS;
        state.offeredQuests = {};
        state.questTokenEarnedToday = false;
        state.dailyChecklistCompleted = { clothes: false, bed: false, dressed: false };
        state.history[today] = { tokens: INITIAL_TOKENS, questsCompleted: [] };
      }
    } else {
      state = getInitialState();
    }
    
    const savedImage = localStorage.getItem('backgroundImage');
    if (savedImage) {
      state.backgroundImage = savedImage;
    }
    
    setAppState(state);
    setIsLoaded(true);
  }, []);

  // Load the initial state only when the user is authenticated.
  useEffect(() => {
    if (user) {
      loadInitialState();
    }
  }, [user, loadInitialState]);

  // Save state to the backend whenever it changes
  useEffect(() => {
    if (isLoaded && appState && !hasSaved.current) {
      // Create a new object from the state, excluding the backgroundImage.
      const { backgroundImage, ...stateToSave } = appState;
      saveAppState(stateToSave as AppState);
      hasSaved.current = true;
    }
  }, [appState, isLoaded]);

  return {
    appState,
    setAppState,
    isLoaded,
    loadInitialState,
    setAppAsLoaded: () => setIsLoaded(true),
  };
};
